import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * Пустая плашка: одно на весь продукт объяснение того, почему список пуст.
 *
 * ⚠️ **До 14.09.2026 компонента пустого состояния не было ни одного**, а
 * трактовок пустоты опись 13.09.2026 насчитала четыре: слова в ячейке, белый
 * холст без слов, фраза `<p className="muted">` внутри карточки и серая
 * подсказка у запертой кнопки. Плашка сводит ТРЕТЬЮ. Остальные три остаются
 * намеренно, и путать их с этой нельзя:
 *
 * - **пустое значение в ЯЧЕЙКЕ говорится словами и без плашки** («не
 *   запланирован», «не задавали», «занятий не было», «без заготовки», «срока
 *   нет»). Плашка встаёт вместо ВСЕГО списка, а не вместо одного слота;
 * - белый холст доски и подсказка у запертой кнопки пустыми списками не
 *   являются вовсе.
 *
 * **Три случая, и выбор между ними механический:**
 *
 * | Случай | Кнопка | Когда |
 * |---|---|---|
 * | пусто совсем | залитая, она же зовёт завести первое | список пуст, и завести первое можно отсюда |
 * | ничего не нашлось | тихая «Очистить поиск» | список не пуст, но отбор не дал ни строки |
 * | показывать пока нечего | нет | список наполняется сам, звать не к чему |
 *
 * ⚠️ **В адрес преподавателя не считается и не пишется ничего** (раздел 5.10
 * CLAUDE.md), и здесь это ловушка номер один. «Учеников пока нет» это факт, а
 * «вы ещё не завели ни одного ученика» это упрёк, и на пустом экране второе
 * прочтение для многих единственное возможное. Проверка одна и стоит десяти
 * секунд: прочитать заголовок вслух. Если получилось про человека, а не про
 * список, переписать.
 *
 * ⚠️ Ни одного восклицательного знака, ни «Упс», ни «Кажется, здесь пусто»:
 * правило текстов целиком в `.claude/skills/teacher-texts`.
 *
 * ⚠️ **Плашка встаёт ВМЕСТО списка, а не под шапкой его колонок.** Решено
 * окончательно 14.09.2026, против артборда B7, где она нарисована внутри
 * `.list`. Довод целиком записан в `styles.css` у `.empty`, здесь коротко: шапка
 * колонок существует только на настольной раскладке, над пустотой она называет
 * колонки, которых нет, а держать её пришлось бы двумя копиями в каждом из
 * двадцати шести мест, где плашку зовут.
 */

/**
 * Словарь значков, и он ЗАКРЫТ.
 *
 * Своя фигура на каждом экране это ровно тот разнобой, ради которого плашка и
 * заводится, только этажом ниже: восемь похожих людей, нарисованных восемью
 * разными руками, читаются хуже одного повторяющегося. Нужен девятый значок
 * значит он добавляется сюда, а не рисуется по месту.
 */
const ICONS = {
  /** Ученики, группы, состав. */
  students: (
    <>
      <path d="M15 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M19 7v6" />
      <path d="M22 10h-6" />
    </>
  ),
  /** Ничего не нашлось: поиск и фильтр. */
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </>
  ),
  /** Занятия, серии, расписание. */
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M3 10h18" />
    </>
  ),
  /** Уроки, заготовки, доски архива, записи «Что нового». */
  board: (
    <>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M7 9h10" />
      <path d="M7 13h6" />
    </>
  ),
  /** Домашние работы. */
  homework: (
    <>
      <path d="M5 4h14v16H5z" />
      <path d="m8 10 2 2 4-4" />
      <path d="M8 16h8" />
    </>
  ),
  /** Материалы преподавателя. */
  folder: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  /** Корзина. */
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V5h6v2" />
      <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
    </>
  ),
  /** Переписка. */
  chat: (
    <>
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    </>
  ),
};

export type EmptyIcon = keyof typeof ICONS;

/**
 * Действие плашки.
 *
 * Либо `to` (переход), либо `onClick` (окно или действие на месте). Оба сразу
 * не имеют смысла, и это тот случай, где типом развилку не выразить дёшево:
 * компонент берёт `to`, если он есть, и не смотрит на второе поле.
 *
 * ⚠️ **Кнопка плашки повторяет главное действие экрана, и это осознанно.** На
 * непустом экране главное действие одно и стоит в правом конце строки
 * заголовка; на пустом оно там же, а плашка зовёт к нему ВТОРОЙ кнопкой в
 * середине. Довод простой: на пустом экране делать больше нечего, а верхний
 * правый угол это последнее место, куда смотрит глаз. Обработчик у обеих кнопок
 * ОДИН, своей копии формы у плашки нет ни на одном экране, и веса кнопки тоже
 * совпадают.
 *
 * Решение стоит вынести владельцу: рядом в `HomeworkListPage` записано его
 * требование «„Задать домашнюю“ стоит в шапке ВСЕГДА, в том числе при пустом
 * списке», и про вторую кнопку там не сказано ни за, ни против.
 */
export interface EmptyAction {
  label: string;
  to?: string;
  onClick?: () => void;
  /**
   * Тихая кнопка вместо залитой. Тихая у случая «ничего не нашлось»: снять
   * запрос это не главное действие экрана, а починка собственного отбора.
   */
  quiet?: boolean;
}

export function Empty(props: {
  icon: EmptyIcon;
  /**
   * Факт о списке, а не о человеке. Читается вслух, и если вышло про человека,
   * переписывается: правило раздела 5.10 CLAUDE.md ломается ровно здесь.
   */
  title: string;
  /** Что из этого не видно. Одна мысль, а не пересказ заголовка другими словами. */
  text?: ReactNode;
  action?: EmptyAction;
}) {
  const act = props.action;
  /* Оба класса целиком, а не `btn` плюс модификатор через шаблон: модификатор
     без базового класса это (0,1,0), то есть слабее любого правила поверхности,
     и замок `lib/button-class.test.ts` такую запись не пропускает */
  const cls = act?.quiet === true ? "btn btn--quiet" : "btn btn--primary";
  return (
    <div className="empty">
      {/* Фигура пустая для читалки: смысл несут заголовок и строка под ним */}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {ICONS[props.icon]}
      </svg>
      <p className="empty__h">{props.title}</p>
      {props.text !== undefined && <p className="empty__p">{props.text}</p>}
      {act !== undefined && (
        <p className="empty__act">
          {act.to !== undefined ? (
            <Link className={cls} to={act.to}>
              {act.label}
            </Link>
          ) : (
            <button type="button" className={cls} onClick={act.onClick}>
              {act.label}
            </button>
          )}
        </p>
      )}
    </div>
  );
}
